/**
 * Native shell detection.
 *
 * The tracker is wrapped by a Capacitor shell (iOS + Android) that loads the
 * LIVE site over `server.url`, so the code running inside the app is byte for
 * byte the code a browser gets. Everything shell-specific therefore has to be
 * decided at runtime, from a marker the shell injects.
 *
 * The marker is Capacitor's own global (`window.Capacitor`), injected by the
 * native runtime before our bundle executes. We deliberately do NOT add
 * `@capacitor/core` to package.json: importing it would ship a second copy of
 * the bridge to every browser visitor and would not make the plugins any more
 * available — the plugin proxies only exist when the native runtime created
 * them.
 *
 * DEV OVERRIDE: `localStorage['pg:tracker:shellOverride'] = 'ios' | 'android'`
 * makes `isNativeShell()`/`shellPlatform()` report a shell in a normal browser,
 * so the hidden-in-shell surfaces can be checked without a device build. It
 * cannot conjure plugins, so `shellPlugin()` still returns null under the
 * override and every capability falls back to its web path — which is exactly
 * what a browser can honour. Documented in docs/tracker/localStorage-keys.md.
 */

export const SHELL_OVERRIDE_KEY = 'pg:tracker:shellOverride';

const PLATFORMS = ['ios', 'android'];

function overridePlatform() {
  try {
    const value = globalThis.localStorage?.getItem(SHELL_OVERRIDE_KEY);
    return PLATFORMS.includes(value) ? value : null;
  } catch {
    // Private mode / storage disabled.
    return null;
  }
}

function capacitor() {
  if (typeof window === 'undefined') return null;
  return window.Capacitor || null;
}

/** True inside the Capacitor shell (or under the dev override). */
export function isNativeShell() {
  if (overridePlatform()) return true;
  try {
    return capacitor()?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/** 'ios' | 'android' inside the shell, null everywhere else. */
export function shellPlatform() {
  const forced = overridePlatform();
  if (forced) return forced;
  if (!isNativeShell()) return null;

  let platform = null;
  try {
    platform = capacitor()?.getPlatform?.() ?? null;
  } catch {
    platform = null;
  }
  return PLATFORMS.includes(platform) ? platform : null;
}

/**
 * A Capacitor plugin proxy by name (`'Haptics'`, `'Share'`, `'KeepAwake'`,
 * `'ScreenOrientation'`), or null when we are not in a shell that registered it.
 * Every caller must treat null as "use the web path".
 */
export function shellPlugin(name) {
  if (!isNativeShell()) return null;
  try {
    return capacitor()?.Plugins?.[name] ?? null;
  } catch {
    return null;
  }
}

/**
 * App mode (docs/reference/app-shell.md): the Capacitor shell, or a page the
 * server rendered in app mode (the home-screen PWA's `pg_app` cookie), which it
 * announces with `<meta name="pg-app-mode" content="true">`. In app mode the
 * store rules apply: no upsell, payment or /support surface, and the tracker's
 * start screen carries the app's tab bar. A rendering hint, never auth.
 */
export const APP_MODE_META = 'pg-app-mode';

export function isAppMode() {
  if (isNativeShell()) return true;
  try {
    return globalThis.document?.querySelector(`meta[name="${APP_MODE_META}"]`)?.content === 'true';
  } catch {
    return false;
  }
}
